/**
 * Rarity Utilities
 * Helper functions for working with item rarity display, colors, and animations
 */

/**
 * Get rarity glow animation class
 * @param {string} rarity - Rarity level (common, uncommon, rare, epic, legendary)
 * @param {boolean} animated - Whether to apply pulse animation
 * @returns {string} CSS class name for rarity glow
 */
export function getRarityGlowClass(rarity, animated = true) {
  if (!rarity || !animated) return '';

  const rarityLower = rarity.toLowerCase();

  const animationMap = {
    'uncommon': 'rarity-glow-uncommon',
    'rare': 'rarity-glow-rare',
    'epic': 'rarity-glow-epic',
    'legendary': 'rarity-glow-legendary',
  };

  return animationMap[rarityLower] || '';
}

/**
 * Get complete rarity styling classes (border, glow, animation)
 * @param {string} rarity - Rarity level
 * @param {boolean} animated - Whether to apply pulse animation
 * @returns {string} Combined CSS classes for rarity styling
 */
export function getRarityClasses(rarity, animated = true) {
  if (!rarity) return 'border-rarity-common';

  const rarityLower = rarity.toLowerCase();

  const baseClasses = {
    'common': 'border-rarity-common',
    'uncommon': 'border-rarity-uncommon shadow-glow-uncommon',
    'rare': 'border-rarity-rare shadow-glow-rare',
    'epic': 'border-rarity-epic shadow-glow-epic',
    'legendary': 'border-rarity-legendary shadow-glow-legendary',
  };

  const baseClass = baseClasses[rarityLower] || baseClasses.common;
  const glowClass = getRarityGlowClass(rarity, animated);

  return `${baseClass} ${glowClass}`.trim();
}

/**
 * Get rarity color variable
 * @param {string} rarity - Rarity level
 * @returns {string} CSS variable name for rarity color
 */
export function getRarityColor(rarity) {
  if (!rarity) return 'var(--color-rarity-common)';

  const rarityLower = rarity.toLowerCase();
  return `var(--color-rarity-${rarityLower})`;
}

/**
 * Check if rarity should have animated glow by default
 * @param {string} rarity - Rarity level
 * @returns {boolean} Whether rarity is high enough for default animation
 */
export function shouldAnimateRarity(rarity) {
  if (!rarity) return false;
  const rarityLower = rarity.toLowerCase();
  return ['epic', 'legendary'].includes(rarityLower);
}
